import {
  CalendarBlank,
  ChatCircle,
  House,
  PlusSquare,
  UserCircle,
  type Icon,
} from '@phosphor-icons/react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { KuniriLogo } from '@/components/kuniri-logo'
import { PushPermissionPrompt } from '@/components/push-permission-prompt'
import { useGlobalChatUnread } from '@/lib/ui/use-global-chat-unread'
import { useUiStore } from '@/lib/ui/ui-store'

import { usePushMaintenance } from './use-push-maintenance'
import { useSwipeTabs } from './use-swipe-tabs'

interface NavItem {
  to: string
  /** Additional routes that should light this tab up. */
  aliases?: readonly string[]
  labelKey: string
  desktopLabelKey?: string
  icon: Icon
  chat?: boolean
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/home',
    aliases: ['/activities'],
    labelKey: 'nav.home',
    icon: House,
  },
  { to: '/saved', labelKey: 'nav.activities', icon: CalendarBlank },
  {
    to: '/create',
    labelKey: 'nav.create',
    desktopLabelKey: 'nav.createLong',
    icon: PlusSquare,
  },
  { to: '/chats', labelKey: 'nav.chats', icon: ChatCircle, chat: true },
  { to: '/profile', labelKey: 'nav.profile', icon: UserCircle },
]

/**
 * Home owns the discovery alias `/activities` (D-052); Attività owns `/saved`.
 * Keeping the alias mapping here makes the tab highlight correct for deep links
 * without adding a second URL to the navigation.
 */
function isCurrent(pathname: string, item: NavItem): boolean {
  if (pathname === item.to) return true
  return item.aliases?.includes(pathname) ?? false
}

function BottomNavigation() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const chatUnread = useUiStore((state) => state.chatUnread)

  return (
    <nav className="bottom-nav" aria-label={t('nav.primaryLabel')}>
      {NAV_ITEMS.map((item) => {
        const NavIcon = item.icon
        const current = isCurrent(pathname, item)
        return (
          <Link
            key={item.to}
            to={item.to}
            className="bottom-nav__link"
            aria-current={current ? 'page' : undefined}
          >
            <span className="bottom-nav__icon">
              <NavIcon size={22} weight="bold" aria-hidden="true" />
              {item.chat && chatUnread ? (
                <span className="bottom-nav__dot" aria-hidden="true" />
              ) : null}
            </span>
            <span>{t(item.labelKey)}</span>
            {item.chat && chatUnread ? (
              <span className="sr-only">{t('nav.newMessages')}</span>
            ) : null}
          </Link>
        )
      })}
    </nav>
  )
}

function DesktopRail() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const chatUnread = useUiStore((state) => state.chatUnread)

  return (
    <aside className="desktop-rail">
      <Link to="/home" className="mx-6 mt-6 mb-7 inline-flex w-fit">
        <KuniriLogo height={40} />
      </Link>
      <p className="eyebrow mb-3 px-6">{t('nav.eyebrow')}</p>
      <nav
        aria-label={t('nav.primaryLabel')}
        className="flex flex-col gap-1 px-4"
      >
        {NAV_ITEMS.map((item) => {
          const NavIcon = item.icon
          const current = isCurrent(pathname, item)
          return (
            <Link
              key={item.to}
              to={item.to}
              className="rail-link"
              aria-current={current ? 'page' : undefined}
            >
              <NavIcon size={22} weight="bold" aria-hidden="true" />
              <span>{t(item.desktopLabelKey ?? item.labelKey)}</span>
              {item.chat && chatUnread ? (
                <>
                  <span className="rail-dot" aria-hidden="true" />
                  <span className="sr-only">{t('nav.newMessages')}</span>
                </>
              ) : null}
            </Link>
          )
        })}
      </nav>
      <p className="mt-auto px-6 pb-6 text-sm leading-relaxed text-muted">
        {t('nav.footer')}
      </p>
    </aside>
  )
}

/**
 * Responsive app shell: 5-tab bottom navigation below 768px, fixed 248px rail
 * above it. Tab routes are children of this layout so future feature work can
 * preserve scroll/draft/map state instead of pushing new stacks (D-043).
 * Below 768px the stage also accepts horizontal swipes between the five tabs
 * (D-058); the gesture never wraps around.
 */
export function AppShell() {
  useGlobalChatUnread()
  usePushMaintenance()
  const { mainRef, stageRef } = useSwipeTabs()

  return (
    <div className="min-h-dvh bg-paper">
      <DesktopRail />
      <main
        ref={mainRef}
        id="main-content"
        tabIndex={-1}
        className="shell-content"
      >
        <div
          ref={stageRef}
          className="tab-swipe-stage"
          data-testid="tab-swipe-stage"
        >
          <Outlet />
        </div>
      </main>
      <BottomNavigation />
      <PushPermissionPrompt />
    </div>
  )
}
